// ===== BEGIN FILE: src/components/PlanetDetailsSidePanel.tsx =====
import React, { useEffect, useState } from 'react';
import { Box, Typography, Sheet, Chip } from '@mui/joy';
import { supabase } from '../utils/supabaseClient';
import { RASHI_NAMES } from '../utils/astroEngine';
import { Info, CheckCircle, Warning, Lightbulb } from '@mui/icons-material';

type PlanetName = 'Sun' | 'Moon' | 'Mercury' | 'Venus' | 'Mars' | 'Jupiter' | 'Saturn' | 'Rahu' | 'Ketu';

type PlanetData = {
  name: PlanetName;
  house: number;
  rasi: number;
  degree: number;
  nakshatra?: string;
  nakshatraPada?: number;
  retro: boolean;
  combust: boolean;
  exalted: boolean;
  debilitated: boolean;
  blendedPct?: number;
  longitude?: number;
};

type AspectData = {
  from: PlanetName;
  toHouse: number;
  kind: string;
};

type Props = {
  planetData: PlanetData | null;
  aspects?: AspectData[];
  lagnaRashi: number;
  getPlanetColor: (planet: PlanetName) => string;
  compact?: boolean; // Compact mode for smaller displays
};

const formatDegree = (deg: number): string => {
  const d = Math.floor(deg);
  const m = Math.floor((deg - d) * 60);
  const s = Math.floor(((deg - d) * 60 - m) * 60);
  return `${d}° ${m}' ${s}"`;
};

const PlanetDetailsSidePanel: React.FC<Props> = ({ 
  planetData, 
  aspects, 
  lagnaRashi,
  getPlanetColor,
  compact = false
}) => {
  const [houseData, setHouseData] = useState<{
    actionable: string | null;
    caution: string | null;
    remedy: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchHouseData = async () => {
      if (!planetData) {
        setHouseData(null);
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('houses')
          .select('*')
          .eq('id', planetData.house)
          .maybeSingle();

        if (error) throw error;
        if (!data) return;

        const planetLower = planetData.name.toLowerCase();
        setHouseData({
          actionable: (data as any)[`actionable_${planetLower}`] || null,
          caution: (data as any)[`caution_${planetLower}`] || null,
          remedy: (data as any)[`remedy_${planetLower}`] || null,
        });
      } catch (err) {
        console.error('Error fetching house data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHouseData();
  }, [planetData]);

  if (!planetData) {
    return (
      <Box
        sx={{
          width: compact ? '100%' : 320,
          height: compact ? 200 : '100%',
          p: compact ? 1.5 : 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center', color: 'text.tertiary' }}>
          <Info sx={{ fontSize: compact ? 36 : 48, mb: 1, opacity: 0.3 }} />
          <Typography level="body-sm" sx={{ fontSize: compact ? '0.75rem' : undefined }}>
            Click on a planet to view details
          </Typography>
        </Box>
      </Box>
    );
  }

  const planetAspects = aspects?.filter(a => a.from === planetData.name) || [];
  const planetColor = getPlanetColor(planetData.name);

  return (
    <Box
      sx={{
        width: compact ? '100%' : 320,
        maxWidth: compact ? 280 : 320,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        p: compact ? 1.5 : 2,
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? 1 : 1.5,
        boxSizing: 'border-box',
      }}
    >
      {/* Planet Header - Ultra compact */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: compact ? 0.5 : 0.5, gap: 0.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Chip
            size={compact ? 'sm' : 'lg'}
            variant="solid"
            sx={{
              bgcolor: planetColor,
              color: 'white',
              fontWeight: 700,
              fontSize: compact ? '0.75rem' : '1rem',
              minWidth: 'auto',
              py: compact ? 0.25 : undefined,
              px: compact ? 1 : 1.5,
              '& .MuiChip-label': {
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }
            }}
          >
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
              <span>{planetData.name}</span>
              {planetData.blendedPct !== undefined && (
                <span style={{ fontSize: compact ? '0.65rem' : '0.85rem', fontWeight: 400 }}>
                  ({planetData.blendedPct >= 0 ? '+' : ''}{planetData.blendedPct.toFixed(0)}%)
                </span>
              )}
            </Box>
          </Chip>
          {planetAspects.length > 0 && (
            <Typography level="body-xs" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: compact ? '0.65rem' : undefined }}>
              👁 {planetAspects.map(a => a.toHouse).join(',')}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Position + Nakshatra/Dignities - Ultra compact */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? 0.5 : 1.5 
      }}>
        {/* Position Compact */}
        <Sheet variant="soft" sx={{ p: compact ? 0.75 : 1.5, borderRadius: 'sm' }}>
          <Typography level="body-xs" sx={{ fontWeight: 600, mb: compact ? 0.25 : 0.5, color: 'text.secondary', fontSize: compact ? '0.65rem' : undefined }}>
            POSITION
          </Typography>
          <Typography level="body-sm" sx={{ fontSize: compact ? '0.7rem' : undefined, lineHeight: compact ? 1.3 : undefined }}>
            H{planetData.house} • {RASHI_NAMES[planetData.rasi - 1]} • {formatDegree(planetData.degree)}
          </Typography>
        </Sheet>

        {/* Nakshatra + Dignities Combined */}
        <Sheet variant="soft" sx={{ p: compact ? 0.75 : 1.5, borderRadius: 'sm' }}>
          <Typography level="body-xs" sx={{ fontWeight: 600, mb: compact ? 0.25 : 0.5, color: 'text.secondary', fontSize: compact ? '0.65rem' : undefined }}>
            NAKSHATRA & STATUS
          </Typography>
          {planetData.nakshatra && (
            <Typography level="body-sm" sx={{ mb: compact ? 0.25 : 0.5, fontSize: compact ? '0.7rem' : '0.75rem', lineHeight: compact ? 1.3 : undefined }}>
              {planetData.nakshatra} {planetData.nakshatraPada ? `(${planetData.nakshatraPada})` : ''}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: compact ? 0.4 : 0.5 }}>
            {planetData.exalted && (
              <Chip size="sm" variant="soft" color="success" sx={{ fontSize: compact ? '0.6rem' : '0.65rem', py: compact ? 0.1 : 0.25, px: compact ? 0.5 : undefined }}>Exalted</Chip>
            )}
            {planetData.debilitated && (
              <Chip size="sm" variant="soft" color="danger" sx={{ fontSize: compact ? '0.6rem' : '0.65rem', py: compact ? 0.1 : 0.25, px: compact ? 0.5 : undefined }}>Debilitated</Chip>
            )}
            {planetData.retro && (
              <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: compact ? '0.6rem' : '0.65rem', py: compact ? 0.1 : 0.25, px: compact ? 0.5 : undefined }}>Retro ᴿ</Chip>
            )}
            {planetData.combust && (
              <Chip size="sm" variant="soft" color="neutral" sx={{ fontSize: compact ? '0.6rem' : '0.65rem', py: compact ? 0.1 : 0.25, px: compact ? 0.5 : undefined }}>Combust</Chip>
            )}
            {!planetData.exalted && !planetData.debilitated && !planetData.retro && !planetData.combust && (
              <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic', fontSize: compact ? '0.65rem' : '0.7rem' }}>
                Normal
              </Typography>
            )}
          </Box>
        </Sheet>
      </Box>

      {/* Actionable */}
      {houseData?.actionable && (
        <Sheet sx={{ 
          p: compact ? 0.75 : 1.5, 
          borderRadius: 'sm', 
          bgcolor: 'success.softBg', 
          borderLeft: compact ? '2px solid' : '3px solid', 
          borderColor: 'success.500', 
          overflow: 'hidden',
          width: '100%',
          maxWidth: compact ? 260 : 300,
          boxSizing: 'border-box'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.4 : 0.5, mb: compact ? 0.3 : 0.5 }}>
            <CheckCircle sx={{ fontSize: compact ? '0.7rem' : '0.875rem', color: 'success.600', flexShrink: 0 }} />
            <Typography level="body-xs" sx={{ fontWeight: 700, color: 'success.700', textTransform: 'uppercase', fontSize: compact ? '0.6rem' : undefined }}>
              Actionable
            </Typography>
          </Box>
          <Typography level="body-xs" sx={{ 
            lineHeight: compact ? 1.3 : 1.5, 
            color: 'text.primary', 
            fontSize: compact ? '0.65rem' : '0.7rem', 
            wordWrap: 'break-word', 
            overflowWrap: 'anywhere',
            whiteSpace: 'normal',
            width: '100%',
            maxWidth: '100%',
            display: 'block',
            hyphens: 'auto',
            wordBreak: 'break-word'
          }}>
            {houseData.actionable}
          </Typography>
        </Sheet>
      )}

      {/* Caution */}
      {houseData?.caution && (
        <Sheet sx={{ 
          p: compact ? 0.75 : 1.5, 
          borderRadius: 'sm', 
          bgcolor: 'warning.softBg', 
          borderLeft: compact ? '2px solid' : '3px solid', 
          borderColor: 'warning.500', 
          overflow: 'hidden',
          width: '100%',
          maxWidth: compact ? 260 : 300,
          boxSizing: 'border-box'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.4 : 0.5, mb: compact ? 0.3 : 0.5 }}>
            <Warning sx={{ fontSize: compact ? '0.7rem' : '0.875rem', color: 'warning.600', flexShrink: 0 }} />
            <Typography level="body-xs" sx={{ fontWeight: 700, color: 'warning.700', textTransform: 'uppercase', fontSize: compact ? '0.6rem' : undefined }}>
              Caution
            </Typography>
          </Box>
          <Typography level="body-xs" sx={{ 
            lineHeight: compact ? 1.3 : 1.5, 
            color: 'text.primary', 
            fontSize: compact ? '0.65rem' : '0.7rem', 
            wordWrap: 'break-word', 
            overflowWrap: 'anywhere',
            whiteSpace: 'normal',
            width: '100%',
            maxWidth: '100%',
            display: 'block',
            hyphens: 'auto',
            wordBreak: 'break-word'
          }}>
            {houseData.caution}
          </Typography>
        </Sheet>
      )}

      {/* Remedy */}
      {houseData?.remedy && (
        <Sheet sx={{ 
          p: compact ? 0.75 : 1.5, 
          borderRadius: 'sm', 
          bgcolor: 'primary.softBg', 
          borderLeft: compact ? '2px solid' : '3px solid', 
          borderColor: 'primary.500', 
          overflow: 'hidden',
          width: '100%',
          maxWidth: compact ? 260 : 300,
          boxSizing: 'border-box'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.4 : 0.5, mb: compact ? 0.3 : 0.5 }}>
            <Lightbulb sx={{ fontSize: compact ? '0.7rem' : '0.875rem', color: 'primary.600', flexShrink: 0 }} />
            <Typography level="body-xs" sx={{ fontWeight: 700, color: 'primary.700', textTransform: 'uppercase', fontSize: compact ? '0.6rem' : undefined }}>
              Remedy
            </Typography>
          </Box>
          <Typography level="body-xs" sx={{ 
            lineHeight: compact ? 1.3 : 1.5, 
            color: 'text.primary', 
            fontSize: compact ? '0.65rem' : '0.7rem', 
            wordWrap: 'break-word', 
            overflowWrap: 'anywhere',
            whiteSpace: 'normal',
            width: '100%',
            maxWidth: '100%',
            display: 'block',
            hyphens: 'auto',
            wordBreak: 'break-word'
          }}>
            {houseData.remedy}
          </Typography>
        </Sheet>
      )}

      {loading && (
        <Typography level="body-xs" sx={{ textAlign: 'center', color: 'text.tertiary', fontStyle: 'italic' }}>
          Loading insights...
        </Typography>
      )}
    </Box>
  );
};

export default PlanetDetailsSidePanel;

// ===== END FILE: src/components/PlanetDetailsSidePanel.tsx =====
